import React, { useState, useRef } from "react";
import { api, API, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Image uploader used in the CMS.
 *
 * Props:
 *  - value: string (single URL) or array of URLs
 *  - onChange: (newValue) => void
 *  - multiple: boolean (if true, value is an array)
 *  - testid?: string
 */
export default function ImageUploader({ value, onChange, multiple = false, testid = "image-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const fileInputRef = useRef(null);

  const items = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const toDisplaySrc = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // Relative /api/media/... — prepend backend
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Falha no upload");
      }
    }
    setUploading(false);
    if (uploaded.length === 0) return;
    if (multiple) {
      onChange([...items, ...uploaded]);
    } else {
      onChange(uploaded[0]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.success(`${uploaded.length} imagem/imagens carregadas.`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    upload(e.dataTransfer.files);
  };

  const removeAt = (i) => {
    if (multiple) {
      onChange(items.filter((_, idx) => idx !== i));
    } else {
      onChange("");
    }
  };

  const addManual = () => {
    const url = manualUrl.trim();
    if (!url) return;
    if (multiple) onChange([...items, url]);
    else onChange(url);
    setManualUrl("");
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDrop}
        className="rounded-xl border border-dashed border-stone-300 bg-stone-50 hover:bg-white transition-colors p-6 text-center cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple={multiple}
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid={`${testid}-input`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-stone-600 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> A carregar...
          </div>
        ) : (
          <div className="text-sm text-stone-600">
            <Upload className="h-5 w-5 mx-auto mb-2 text-[#103C4A]" />
            Arraste imagens para aqui ou clique para escolher
            <div className="text-xs text-stone-400 mt-1">JPG, PNG, WEBP · até 10MB</div>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className={multiple ? "grid grid-cols-3 sm:grid-cols-4 gap-3" : "grid grid-cols-1 gap-3"}>
          {items.map((url, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-stone-200 aspect-[4/3] bg-stone-100">
              <img src={toDisplaySrc(url)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                data-testid={`${testid}-remove-${i}`}
                className="absolute top-1 right-1 rounded-full bg-white/95 p-1 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remover"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 items-center pt-1">
        <input
          type="text"
          value={manualUrl}
          onChange={(e) => setManualUrl(e.target.value)}
          placeholder="ou colar URL externo (https://...)"
          data-testid={`${testid}-manual-url`}
          className="flex-1 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs"
        />
        <button
          type="button"
          onClick={addManual}
          data-testid={`${testid}-manual-add`}
          className="btn-ghost text-xs"
        >
          Adicionar URL
        </button>
      </div>
    </div>
  );
}
